import { io, Socket } from 'socket.io-client';
import { DatasetOverview } from '../types/training'; // Import the new type
import supabase from './supabaseClient';

// Define types for our data
export interface FileInfo {
  name: string;
  type: string;
  size: number;
  content: string | null;
  annotations?: string | null; // JSON stringified annotations
  marks?: string | null; // JSON stringified marks
}

export interface BoxData {
  // Use more descriptive names for the boxes
  inputFiles: FileInfo[];      // topLeft
  outputFiles: FileInfo[];     // topRight
  inputExamples: FileInfo[];   // bottomLeft
  outputExamples: FileInfo[];  // bottomRight
  prompt: string;
  useGptCache?: boolean;       // Use cached GPT results (default: true)
  useModelCache?: boolean;     // Use cached model code (default: true)
  modelName?: string;          // Model name from config window
  gpuType?: string;            // GPU type preference (auto, cuda, cpu, mps)
}

export interface ModelUpdateResponse {
  status: string;
  message: string;
  modelStructure: {
    inputProcessing: any[];
    outputProcessing: any[];
    modelArchitecture: any;
  };
}

export interface BoxDataResponse {
  status: string;
  message?: string;
  data?: any;
}

export interface TrainingProgressData {
  type: 'progress';
  epoch: number;
  train_loss: number;
  val_loss: number;
  activations?: any; // Optional: layer activations for visualization (large data, excluded from localStorage)
  weight_changes?: any; // Optional: weight/bias changes for backprop visualization (large data, excluded from localStorage)
}

class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Function[]> = new Map();

  // Connect to the WebSocket server
  // Use environment variable if available, otherwise fallback to default
  // Supports both http:// and ws:// protocols (socket.io handles both)
  async connect(url?: string): Promise<void> {
    // Use the same server as the main websocket (port 5000 by default)
    // Training handlers are on the same server, just different events
    const defaultTrainingUrl = process.env.REACT_APP_WS_URL || process.env.REACT_APP_WEBSOCKET_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    const connectionUrl = url || defaultTrainingUrl;

    // Don't reconnect if already connected to the same URL
    if (this.socket && this.socket.connected) {
      console.log('[SocketService] Already connected, skipping reconnect');
      return;
    }

    // Only disconnect if socket exists but is not connected (cleanup stale connection)
    if (this.socket && !this.socket.connected) {
      console.log('[SocketService] Cleaning up disconnected socket before reconnecting');
      this.disconnect();
    }
    // If socket doesn't exist, proceed with connection

    try {
      // Get authentication token from Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || null;

      if (!token) {
        console.error('No authentication token available. Please sign in.');
        return;
      }

      // Use EXACT same configuration as the working visualization websocket
      // Increased timeouts and buffers for long-running training operations with high-frequency batch updates
      this.socket = io(connectionUrl, {
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 60000, // 60 seconds timeout
        transports: ['websocket'], // Use websocket only - no upgrade, no disconnects
        upgrade: false, // No upgrade needed (only one transport)
        // Pass token as query parameter for authentication
        query: {
          token: token
        },
        // Also pass in extraHeaders as fallback
        extraHeaders: {
          'Authorization': `Bearer ${token}`
        },
        // Increase buffer sizes for high-frequency batch updates
        perMessageDeflate: {
          threshold: 1024 // Compress messages > 1KB
        }
        // Note: maxHttpBufferSize is server-side only (configured in backend/main.py)
      });

      this.socket.on('connect', () => {
        console.log('[SocketService] Connection established');
        this.notifyListeners('connect', null); // Notify Context that we're connected
      });

      this.socket.on('connect_error', (error) => {
        console.error('[SocketService] Connection error:', error);
      });

      this.socket.on('disconnect', (reason: string) => {
        console.log(`[SocketService] Connection lost. Reason: ${reason}`);
        this.notifyListeners('disconnect', reason); // Notify Context of disconnection
      });

      this.socket.on('reconnect_attempt', (attemptNumber: number) => {
        console.log(`[SocketService] Reconnection attempt ${attemptNumber}`);
      });

      this.socket.on('reconnect', (attemptNumber: number) => {
        console.log(`[SocketService] Reconnected after ${attemptNumber} attempts`);
      });

      this.socket.on('error', (error) => {
        console.error('WebSocket error:', error);
        this.notifyListeners('error', error);
      });

      // Add listeners for server events
      this.socket.on('modelUpdate', (data) => {
        this.notifyListeners('modelUpdate', data);
      });

      this.socket.on('projectExists', (data) => {
        this.notifyListeners('projectExists', data);
      });

      this.socket.on('trainingCancelled', (data) => {
        this.notifyListeners('trainingCancelled', data);
      });

      this.socket.on('boxDataResponse', (data) => {
        this.notifyListeners('boxDataResponse', data);
      });

      this.socket.on('trainingProgress', (data: TrainingProgressData) => {
        this.notifyListeners('trainingProgress', data);
      });

      this.socket.on('trainingStarted', (data: any) => {
        this.notifyListeners('trainingStarted', data);
      });

      this.socket.on('trainingCompleted', (data: any) => {
        this.notifyListeners('trainingCompleted', data);
      });

      this.socket.on('trainingError', (data: any) => {
        console.error('Received trainingError event from server:', data);
        this.notifyListeners('trainingError', data);
      });

      this.socket.on('datasetOverview', (data: DatasetOverview) => {
        this.notifyListeners('datasetOverview', data);
      });

      this.socket.on('modelStructure', (data: any) => {
        this.notifyListeners('modelStructure', data);
      });

      this.socket.on('trainingCodeResponse', (data: any) => {
        console.log('Received trainingCodeResponse from server:', data);
        this.notifyListeners('trainingCodeResponse', data);
      });

      this.socket.on('batchProgress', (data: any) => {
        this.notifyListeners('batchProgress', data);
      });

      this.socket.on('trainingPaused', (data: any) => {
        this.notifyListeners('trainingPaused', data);
      });

      this.socket.on('trainingResumed', (data: any) => {
        this.notifyListeners('trainingResumed', data);
      });

      this.socket.on('layerDelayUpdated', (data: any) => {
        this.notifyListeners('layerDelayUpdated', data);
      });

      this.socket.on('modelEvaluationInfo', (data: any) => {
        this.notifyListeners('modelEvaluationInfo', data);
      });

      this.socket.on('reevaluationTriggered', (data: any) => {
        this.notifyListeners('reevaluationTriggered', data);
      });

      this.socket.on('reevaluationComplete', (data: any) => {
        this.notifyListeners('reevaluationComplete', data);
      });

    } catch (error) {
      console.error('Error initializing Socket.IO:', error);
    }
  }

  // Disconnect from the WebSocket server
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // Send file data to the server for processing to generate model structure
  processFiles(files: any): void {
    if (!this.socket) {
      console.error('Cannot process files: Not connected to server');
      return;
    }
    this.socket.emit('processFiles', files);
  }

  // Confirm overwrite of existing project
  confirmOverwrite(data: { overwrite: boolean; continueWithOld?: boolean; modelName: string; originalData: any }): void {
    if (!this.socket) {
      console.error('Cannot confirm overwrite: Not connected to server');
      return;
    }
    this.socket.emit('confirmOverwrite', data);
  }

  // Send box data to the server for processing using the GPT API
  processBoxData(data: BoxData): void {
    console.log('[SOCKET SERVICE] processBoxData called');
    console.log('[SOCKET SERVICE] Socket exists:', !!this.socket);
    console.log('[SOCKET SERVICE] Socket connected:', this.socket?.connected);
    
    if (!this.socket) {
      console.error('[SOCKET SERVICE] Cannot process box data: Not connected to server');
      return;
    }

    if (!this.socket.connected) {
      console.error('[SOCKET SERVICE] Cannot process box data: Socket not connected');
      return;
    }

    // Add explicit backend-friendly format for annotations/marks
    // This helps ensure the backend will recognize and print them
    const enhancedData = {
      ...data,
      debugInfo: {
        annotationSummary: this.summarizeAnnotations(data)
      }
    };

    console.log('[SOCKET SERVICE] Emitting processBoxData event with data:', {
      inputFiles: enhancedData.inputFiles.length,
      outputFiles: enhancedData.outputFiles.length,
      inputExamples: enhancedData.inputExamples.length,
      outputExamples: enhancedData.outputExamples.length
    });

    // Send the actual data
    this.socket.emit('processBoxData', enhancedData, (response: any) => {
      console.log('[SOCKET SERVICE] processBoxData acknowledgement received:', response);
    });
    
    console.log('[SOCKET SERVICE] processBoxData emit completed');
  }

  // Helper method to create summary of annotations for the backend to display
  private summarizeAnnotations(data: BoxData): any {
    const summary: any = {
      filesWithAnnotations: [],
      filesWithMarks: []
    };

    // Process all file collections
    [
      { type: 'inputFile', files: data.inputFiles },
      { type: 'outputFile', files: data.outputFiles },
      { type: 'inputExample', files: data.inputExamples },
      { type: 'outputExample', files: data.outputExamples }
    ].forEach(collection => {
      collection.files.forEach((file, index) => {
        if (file.annotations && file.annotations.length > 10) { // Has annotation data
          try {
            const parsed = JSON.parse(file.annotations);
            const annotationCount = Array.isArray(parsed) ? parsed.length : 0;
            if (annotationCount > 0) {
              summary.filesWithAnnotations.push({
                fileType: collection.type,
                index,
                name: file.name,
                type: file.type,
                annotationCount,
                details: parsed.map((a: any) =>
                  'x' in a ?
                    `BOX: (${a.x},${a.y}) ${a.width}x${a.height}` :
                    `POLYGON: ${a.points.length} points`
                )
              });
            }
          } catch (e) {
            console.error('Error parsing annotations:', e);
          }
        }

        if (file.marks && file.marks.length > 10) { // Has mark data
          try {
            const parsed = JSON.parse(file.marks);
            const markCount = Array.isArray(parsed) ? parsed.length : 0;
            if (markCount > 0) {
              summary.filesWithMarks.push({
                fileType: collection.type,
                index,
                name: file.name,
                type: file.type,
                markCount,
                details: parsed.map((m: any) =>
                  `MARK: ${m.start}-${m.end}, color: ${m.color}`
                )
              });
            }
          } catch (e) {
            console.error('Error parsing marks:', e);
          }
        }
      });
    });

    return summary;
  }

  // Add an event listener
  on<T>(eventName: 'connect' | 'disconnect' | 'modelUpdate' | 'boxDataResponse' | 'trainingProgress' | 'trainingStarted' | 'trainingCompleted' | 'trainingError' | 'datasetOverview' | 'modelStructure' | 'trainingCodeResponse' | 'batchProgress' | 'trainingPaused' | 'trainingResumed' | 'layerDelayUpdated' | 'error' | 'projectExists' | 'trainingCancelled' | 'modelEvaluationInfo' | 'reevaluationTriggered' | 'reevaluationComplete', callback: (data: T) => void): void {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, []);
    }
    const listeners = this.listeners.get(eventName)!;
    listeners.push(callback as Function);
    
    // Debug: Warn if too many listeners accumulate (indicates leak)
    if (listeners.length > 5 && eventName === 'trainingProgress') {
      console.warn(`[SocketService] Warning: ${listeners.length} trainingProgress listeners registered. Expected 1-2.`);
    }
  }

  // Remove an event listener
  off(event: 'connect' | 'disconnect' | 'modelUpdate' | 'boxDataResponse' | 'trainingProgress' | 'trainingStarted' | 'trainingCompleted' | 'trainingError' | 'datasetOverview' | 'modelStructure' | 'trainingCodeResponse' | 'batchProgress' | 'trainingPaused' | 'trainingResumed' | 'layerDelayUpdated' | 'error' | 'projectExists' | 'trainingCancelled' | 'modelEvaluationInfo' | 'reevaluationTriggered' | 'reevaluationComplete' | string, callback: Function): void {
    if (!this.listeners.has(event)) {
      return;
    }
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      this.listeners.set(
        event,
        callbacks.filter((cb) => cb !== callback)
      );
    }
  }

  // Notify all listeners for an event
  private notifyListeners(event: string, data: any): void {
    if (!this.listeners.has(event)) {
      return;
    }
    const callbacks = this.listeners.get(event);
    if (callbacks && callbacks.length > 0) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (e) {
          console.error(`SocketService: Error in listener callback for event '${event}':`, e);
        }
      });
    }
  }

  // Check if socket is connected
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Public method to emit a generic event
  emit(event: string, data?: any): void {
    if (!this.socket) {
      console.error(`Cannot emit event '${event}': Not connected to server`);
      return;
    }
    this.socket.emit(event, data);
  }

  // Request the dataset overview from the backend
  getDatasetOverview(): void {
    if (!this.socket) {
      console.error('Cannot get dataset overview: Not connected to server');
      return;
    }
    this.socket.emit('getDatasetOverview');
  }

  // Pause training
  pauseTraining(): void {
    if (!this.socket) {
      console.error('Cannot pause training: Not connected to server');
      return;
    }
    this.socket.emit('pauseTraining');
  }

  // Resume training
  resumeTraining(): void {
    if (!this.socket) {
      console.error('Cannot resume training: Not connected to server');
      return;
    }
    this.socket.emit('resumeTraining');
  }

  // Cancel training (works for both local and RunPod)
  // Made async to support reconnection if needed for critical cancellation
  async cancelTraining(job_id?: string): Promise<void> {
    // If not connected, try to reconnect first (cancellation is critical)
    if (!this.socket || !this.socket.connected) {
      console.warn('[SocketService] Not connected, attempting to reconnect for cancellation...');
      try {
        await this.connect();
        // Wait a moment for connection to establish
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('[SocketService] Failed to reconnect for cancellation:', error);
        // Still try to emit - might work if connection just established
      }
    }

    if (!this.socket) {
      console.error('Cannot cancel training: Failed to establish connection');
      return;
    }

    if (!this.socket.connected) {
      console.error('Cannot cancel training: Socket not connected after reconnect attempt');
      return;
    }

    if (job_id) {
      this.socket.emit('cancelTraining', { job_id });
      console.log(`[SocketService] Emitted cancelTraining for job_id: ${job_id}`);
    } else {
      this.socket.emit('cancelTraining');
      console.log('[SocketService] Emitted cancelTraining (no job_id)');
    }
  }

  // Set layer delay in milliseconds
  setLayerDelay(delayMs: number): void {
    if (!this.socket) {
      console.error('Cannot set layer delay: Not connected to server');
      return;
    }
    this.socket.emit('setLayerDelay', { delay: delayMs });
  }

  // Get the socket instance (for direct access if needed)
  getSocket(): Socket | null {
    return this.socket;
  }

  // Request current model evaluation information
  requestModelEvaluationInfo(): void {
    if (!this.socket) {
      console.error('Cannot request evaluation info: Not connected to server');
      return;
    }
    this.socket.emit('getModelEvaluationInfo');
  }

  // Trigger manual model reevaluation
  triggerManualReevaluation(): void {
    if (!this.socket) {
      console.error('Cannot trigger reevaluation: Not connected to server');
      return;
    }
    this.socket.emit('triggerManualReevaluation');
  }
}

// Export a singleton instance
export const socketService = new SocketService();
